import React, { useMemo } from 'react';
import { Badge, Highlight, Text, Button, Flex } from '@mantine/core';
import { IconUserPlus } from '@tabler/icons-react';

import { useOrganisationSearch } from '../../context/OrganisationSearchContext';
import arrow from '../../assets/arrow.svg';
import stringifyNumber from '../../utility/stringifyNumber';
import FranchiseBadge from './FranchiseBadge';
import StoreManagerDetails from './StoreManagerDetails';
import { StoreWithManagers } from '../../ts/interfaces/stores/store';
import { useUserPortal } from '../../context/UserPortalContext';
import { useAuth } from '../../context/AuthProvider';
import { checkUserRole } from '../../utility/userRoles';
import { OpsDivision } from '../../codegen/graphql';
import { UserRole } from '../../types/users';

type Props = Readonly<{
    store: StoreWithManagers;
    division: OpsDivision;
}>;

function StoreCard({ store, division }: Props) {
    const { organisationSearch } = useOrganisationSearch();
    const { openTeamsModal } = useUserPortal();

    const { authUser } = useAuth();

    const isAdmin = checkUserRole(authUser, [
        UserRole.ADMIN,
        UserRole.SUPER_ADMIN,
    ]);
    const isDivisionalManager = checkUserRole(authUser, [
        UserRole.DIVISIONAL_MANAGER,
    ]);

    const userCanManageStore = useMemo(() => {
        if (isAdmin) {
            return true;
        }
        if (!isDivisionalManager) {
            return false;
        }

        const divisionIds =
            authUser?.managerDivision?.map(({ id }) => id) || [];
        if (divisionIds.includes(division?.id || '')) {
            return true;
        }

        return division?.manager?.id === authUser?.id;
    }, [isAdmin, isDivisionalManager, authUser, division]);

    const borderColor = useMemo(() => {
        if (!store.managers || !store.managers.length) {
            return '#D92A1C';
        }
        return '#7EACFA';
    }, [store]);

    const address = useMemo(() => {
        const parts = [store.streetAddress, store.town, store.postCode].filter(
            Boolean,
        );
        return parts.length > 0 ? parts.join(', ') : 'N/A';
    }, [store]);

    return (
        <div className="grid grid-rows-1 grid-cols-24 relative h-16 items-center">
            <div className="flex flex-col h-full col-span-4">
                <img
                    src={arrow}
                    alt="L-arrow"
                    className="w-6 absolute h-24 -top-11 left-0"
                />
                <div
                    style={{
                        borderLeft: `4px solid ${borderColor}`,
                    }}
                    className="ml-[22px] h-full bg-white flex flex-col justify-center p-2"
                >
                    <Highlight
                        fw="bold"
                        size="md"
                        highlight={organisationSearch}
                    >
                        {store.name ?? ''}
                    </Highlight>
                    <div className="flex items center gap-2">
                        <Badge size="xs" color="gray" variant="light">
                            <Highlight
                                size="xs"
                                className="font-bold"
                                highlight={organisationSearch}
                            >
                                {store.storeNo ?? ''}
                            </Highlight>
                        </Badge>
                        <FranchiseBadge franchise={store.franchise || 'N/A'} />
                    </div>
                </div>
            </div>

            <div className="col-span-1 bg-white h-full flex flex-col justify-center p-2">
                <Text className="whitespace-nowrap text-ellipsis overflow-hidden text-sm">
                    <Highlight
                        className="text-sm"
                        highlight={organisationSearch}
                    >
                        {store.speedDial || 'N/A'}
                    </Highlight>
                </Text>
            </div>

            <div className="col-span-3 bg-white h-full flex flex-col justify-center p-2">
                {store.telNo ? (
                    <a
                        href={`tel:${stringifyNumber(store.telNo)}`}
                        className="hover:underline whitespace-nowrap text-ellipsis overflow-hidden text-sm text-primary"
                    >
                        <Highlight
                            className="text-sm"
                            highlight={organisationSearch}
                        >
                            {stringifyNumber(store.telNo)}
                        </Highlight>
                    </a>
                ) : (
                    <p className="whitespace-nowrap text-ellipsis overflow-hidden text-sm">
                        N/A
                    </p>
                )}
            </div>

            <div className="col-span-5 bg-white h-full flex flex-col justify-center p-2">
                {store.email ? (
                    <a
                        href={`mailto:${store.email}`}
                        className="hover:underline whitespace-nowrap text-ellipsis overflow-hidden text-sm text-primary"
                    >
                        <Highlight
                            className="text-sm"
                            highlight={organisationSearch}
                        >
                            {store.email ?? ''}
                        </Highlight>
                    </a>
                ) : (
                    <p className="whitespace-nowrap text-ellipsis overflow-hidden text-sm">
                        N/A
                    </p>
                )}
            </div>

            <div className="col-span-5 bg-white h-full flex flex-col justify-center p-2">
                <Text className="whitespace-normal text-ellipsis overflow-hidden text-sm">
                    <Highlight
                        className="text-sm"
                        highlight={organisationSearch}
                    >
                        {address}
                    </Highlight>
                </Text>
            </div>

            <div className="col-span-5 bg-white h-full flex flex-row items-center justify-between p-2">
                {store.managers?.length ? (
                    <StoreManagerDetails
                        store={store}
                        userCanManageStore={userCanManageStore}
                    />
                ) : (
                    <Flex className="flex justify-between items-center w-full">
                        <Text color="#e06565">No manager associated</Text>
                        {userCanManageStore && (
                            <Button
                                variant="subtle"
                                size="xs"
                                className="text-primary rounded-md p-1 w-min h-min mr-5"
                                onClick={() =>
                                    openTeamsModal({
                                        addingStoreManager: true,
                                        store,
                                    })
                                }
                            >
                                <IconUserPlus size={20} />
                            </Button>
                        )}
                    </Flex>
                )}
            </div>
        </div>
    );
}

export default StoreCard;
